import { useEffect, useMemo, useRef } from "react"

import DOMPurify from "dompurify"
import { useTranslation } from "react-i18next"
import { parse } from "tldts"

import type { ExtraDataItem } from "../hooks/use-citation"

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"

type MetadataDialogProps = {
  openMetadataDialog: boolean
  setOpenMetadataDialog: (open: boolean) => void
  extraData: ExtraDataItem[] | null
}

/**
 * The display mode for a metadata value. `render` supersedes the older `links` boolean, which is
 * still honoured so libraries configured before the mode existed keep rendering their links.
 */
function renderModeOf(item: ExtraDataItem): "text" | "html" | "link" {
  if (item.render === "html" || item.render === "link" || item.render === "text") return item.render
  return item.links ? "link" : "text"
}

/**
 * Tightened beyond DOMPurify's defaults, which allow `style`, `class`, `form` and form controls.
 *
 * The author here is whoever can edit the SharePoint list -- not a Hello AI admin -- and a
 * `<style>` injected through innerHTML is not scoped to its container, so it applies to the whole
 * page and can hide or reposition anything around it. A `<form>` with inputs gives that same
 * person a credible credential prompt inside a dialog the reader trusts. Neither is script
 * execution (DOMPurify blocks that by default); both are defacement and phishing reachable by a
 * lower-privileged author than this feature otherwise assumes.
 *
 * `class` is forbidden for the same reason as `style`: this is a Tailwind app, so the utilities
 * that position and colour our own UI are already in the bundle, and a class list is enough to
 * compose an overlay covering the dialog with a button-styled link. `id` goes with it -- it
 * collides with our own ids and the `aria-*` references between them. Nothing is lost: the only
 * class SharePoint emits is its own `ExternalClass...` wrapper, meaningless outside SharePoint.
 */
const SANITIZE_OPTIONS = {
  FORBID_TAGS: ["style", "form", "input", "button", "textarea", "select"],
  FORBID_ATTR: ["style", "class", "id"],
}

/**
 * A SharePoint rich-text column returns HTML, so it has to be rendered rather than printed -- but
 * that markup is never trusted (#3008).
 */
function HtmlValue({ html }: { html: string }) {
  const clean = useMemo(() => DOMPurify.sanitize(html, SANITIZE_OPTIONS), [html])
  const containerRef = useRef<HTMLDivElement>(null)

  // DOMPurify drops `target`, so a link inside the markup would replace the app in this tab and
  // lose the conversation -- while the `link` mode right beside it opens a new tab. Set it on the
  // rendered anchors instead of re-allowing the attribute globally, which would need `rel` policed
  // too (reverse tabnabbing) and would leak into every other DOMPurify caller in the app.
  useEffect(() => {
    const anchors = containerRef.current?.querySelectorAll("a[href]")
    anchors?.forEach((anchor) => {
      anchor.setAttribute("target", "_blank")
      anchor.setAttribute("rel", "noopener noreferrer")
    })
  }, [clean])

  return (
    <div
      ref={containerRef}
      className="prose prose-sm dark:prose-invert max-w-none break-words"
      // `clean` is DOMPurify output.
      dangerouslySetInnerHTML={{ __html: clean }}
      data-testid="metadata-html-value"
    />
  )
}

function getDomainFromUrl(url: string): string {
  const parsed = parse(url)
  if (parsed.domain) {
    return parsed.domain
  }
  // If parsing fails, return the original URL or handle accordingly
  return url
}

function MetadataValue({
  mode,
  value,
  linkTitle,
}: {
  mode: "text" | "html" | "link"
  value: string
  linkTitle?: string
}) {
  if (mode === "link") {
    return (
      <a
        className="text-primary cursor-pointer underline-offset-4 hover:underline"
        href={value}
        rel="noopener noreferrer"
        target="_blank"
      >
        {linkTitle || getDomainFromUrl(value)}
      </a>
    )
  }
  if (mode === "html") return <HtmlValue html={value} />
  return <>{value}</>
}

export function MetadataDialog({
  openMetadataDialog,
  setOpenMetadataDialog,
  extraData,
}: MetadataDialogProps) {
  const { t } = useTranslation()

  return (
    <Dialog open={openMetadataDialog} onOpenChange={setOpenMetadataDialog}>
      <DialogContent className="flex max-h-[600px] flex-col sm:max-w-screen-lg">
        <DialogHeader>
          <DialogTitle>{t("answer.metadata")}</DialogTitle>
        </DialogHeader>
        {extraData !== null && (
          <section className="grid grid-cols-2 justify-center gap-4 overflow-auto md:grid-cols-3 lg:grid-cols-4">
            {extraData.map((item, index) => {
              if (item.add_to_ui && item.values.length > 0 && item.values[0] != "") {
                return (
                  <div key={index}>
                    <p className="font-bold">{item.display_name}</p>
                    <ul>
                      {item.values.map((value, valueIndex) => {
                        const url = String(value)
                        const mode = renderModeOf(item)
                        const linkTitle = item.link_titles?.[valueIndex]

                        return (
                          <li
                            key={valueIndex}
                            className={mode === "html" ? "break-words" : "break-all"}
                          >
                            <MetadataValue linkTitle={linkTitle} mode={mode} value={url} />
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )
              }
              return null
            })}
          </section>
        )}
      </DialogContent>
    </Dialog>
  )
}
